/**
 * ShareButtons — drop-in social share row for SEO pages.
 *
 * Why this exists: the Ecowitt → Rachio page is the answer to a question
 * that's been sitting unanswered on the Rachio Community forum for 2 years.
 * Every visitor who lands here is also a potential distributor — they
 * almost certainly know someone in a FB lawn group or a Reddit thread
 * who has the same problem.
 *
 * The buttons emit pre-filled text + the canonical URL so a click opens
 * the destination already half-composed. The "Copy link" button uses
 * the Clipboard API with a sonner toast confirmation.
 *
 * Design rules:
 *   - Tiny footprint (single horizontal row), zero hype.
 *   - data-testids on every button for the testing agent.
 *   - All four destinations open in a new tab with noopener/noreferrer.
 */
import React, { useState } from 'react';
import { Facebook, Linkedin, Link as LinkIcon, Check } from 'lucide-react';
import { toast } from 'sonner';

const BTN = 'inline-flex items-center gap-1.5 rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-1.5 text-xs font-semibold text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800 hover:border-emerald-500 dark:hover:border-emerald-500 transition-colors';

export const ShareButtons = ({
  url,
  title = 'How to connect Ecowitt soil sensors to Rachio',
  message = 'Rachio still has no native Ecowitt integration. We built the bridge — your Rachio fires only when the soil agrees.',
  testIdPrefix = 'share',
}) => {
  const [copied, setCopied] = useState(false);
  const encodedUrl = encodeURIComponent(url);
  const encodedMessage = encodeURIComponent(`${message} ${url}`);
  const encodedTitle = encodeURIComponent(title);

  const fbHref = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodeURIComponent(message)}`;
  const xHref = `https://twitter.com/intent/tweet?text=${encodedMessage}`;
  const redditHref = `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`;
  const liHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${message} ${url}`);
      setCopied(true);
      toast.success('Link + message copied — paste it anywhere');
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      toast.error('Could not copy. Long-press to copy manually.');
    }
  };

  return (
    <div
      className="flex flex-wrap items-center gap-2"
      data-testid={`${testIdPrefix}-buttons`}
    >
      <span className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 dark:text-zinc-400 mr-1">
        Help someone find this
      </span>
      <a
        href={fbHref}
        target="_blank"
        rel="noopener noreferrer"
        className={BTN}
        data-testid={`${testIdPrefix}-facebook`}
      >
        <Facebook className="w-3.5 h-3.5" /> Facebook
      </a>
      <a
        href={xHref}
        target="_blank"
        rel="noopener noreferrer"
        className={BTN}
        data-testid={`${testIdPrefix}-x`}
      >
        <span className="font-black text-sm leading-none">𝕏</span> Post
      </a>
      <a
        href={redditHref}
        target="_blank"
        rel="noopener noreferrer"
        className={BTN}
        data-testid={`${testIdPrefix}-reddit`}
      >
        <span className="font-black text-xs leading-none text-orange-600">r/</span> Reddit
      </a>
      <a
        href={liHref}
        target="_blank"
        rel="noopener noreferrer"
        className={BTN}
        data-testid={`${testIdPrefix}-linkedin`}
      >
        <Linkedin className="w-3.5 h-3.5" /> LinkedIn
      </a>
      <button
        type="button"
        onClick={handleCopy}
        className={BTN}
        data-testid={`${testIdPrefix}-copy`}
      >
        {copied ? (
          <>
            <Check className="w-3.5 h-3.5 text-emerald-600" /> Copied
          </>
        ) : (
          <>
            <LinkIcon className="w-3.5 h-3.5" /> Copy link
          </>
        )}
      </button>
    </div>
  );
};

export default ShareButtons;
